import { lazy, Suspense, type ReactNode } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { AppShell } from '@/components/layout'
import { ProtectedRoute } from '@/components/auth/protected-route'
import { useAuth } from '@/features/auth/auth-context'
import { NotFoundPage } from '@/pages/not-found'
import { RoleGuard } from '@/features/admin/components/role-guard'

/**
 * Retry wrapper for lazy imports — handles transient CDN / deploy failures.
 * On failure, waits briefly then retries up to `maxRetries` times.  If all
 * retries fail, forces a page reload (clears stale chunk cache).
 */
function lazyRetry<T extends React.ComponentType<unknown>>(
  importFn: () => Promise<{ default: T }>,
  maxRetries = 2,
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await importFn()
      } catch (err) {
        if (attempt === maxRetries) {
          // Last attempt failed — reload page to bust stale chunk cache
          const hasReloaded = sessionStorage.getItem('chunk-reload')
          if (!hasReloaded) {
            sessionStorage.setItem('chunk-reload', '1')
            window.location.reload()
          }
          throw err
        }
        await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)))
      }
    }
    // Unreachable — satisfies TypeScript
    throw new Error('lazyRetry exhausted')
  })
}

// All pages are lazy-loaded to keep the main bundle small and improve INP.
// Only tiny utility components (NotFoundPage, RoleGuard) are eagerly imported.
const HomePage = lazyRetry(() => import('@/pages/home').then((m) => ({ default: m.HomePage })))
const ModuleHubPage = lazyRetry(() =>
  import('@/pages/module-hub').then((m) => ({ default: m.ModuleHubPage })),
)
const AuthPage = lazyRetry(() => import('@/pages/auth').then((m) => ({ default: m.AuthPage })))
const ProfilePage = lazyRetry(() =>
  import('@/pages/profile').then((m) => ({ default: m.ProfilePage })),
)
const WalletPage = lazyRetry(() =>
  import('@/pages/wallet').then((m) => ({ default: m.WalletPage })),
)
const CertificateDetailPage = lazyRetry(() =>
  import('@/pages/certificate-detail').then((m) => ({ default: m.CertificateDetailPage })),
)
const ArmouryPage = lazyRetry(() =>
  import('@/pages/armoury').then((m) => ({ default: m.ArmouryPage })),
)
const FirearmDetailPage = lazyRetry(() =>
  import('@/pages/firearm-detail').then((m) => ({ default: m.FirearmDetailPage })),
)
const ClubsPage = lazyRetry(() => import('@/pages/clubs').then((m) => ({ default: m.ClubsPage })))
const DiscoverPage = lazyRetry(() =>
  import('@/pages/discover').then((m) => ({ default: m.DiscoverPage })),
)
const ShootLogPage = lazyRetry(() =>
  import('@/pages/shoot-log').then((m) => ({ default: m.ShootLogPage })),
)
const StatsPage = lazyRetry(() => import('@/pages/stats').then((m) => ({ default: m.StatsPage })))
const TeamsPage = lazyRetry(() => import('@/pages/teams').then((m) => ({ default: m.TeamsPage })))
const TrainingPage = lazyRetry(() =>
  import('@/pages/training').then((m) => ({ default: m.TrainingPage })),
)
const TrainingAdminPage = lazyRetry(() =>
  import('@/pages/training-admin').then((m) => ({ default: m.TrainingAdminPage })),
)

// Lazy-loaded heavy pages — use lazyRetry for resilience to transient CDN failures
const CompetitionsPage = lazyRetry(() =>
  import('@/pages/competitions').then((m) => ({ default: m.CompetitionsPage })),
)
const ResultsPage = lazyRetry(() =>
  import('@/pages/results').then((m) => ({ default: m.ResultsPage })),
)
const NotificationsPage = lazyRetry(() =>
  import('@/pages/notifications').then((m) => ({ default: m.NotificationsPage })),
)
const AdminPage = lazyRetry(() => import('@/pages/admin').then((m) => ({ default: m.AdminPage })))
const PaymentsPage = lazyRetry(() =>
  import('@/pages/payments').then((m) => ({ default: m.PaymentsPage })),
)
const SupportPage = lazyRetry(() =>
  import('@/pages/support').then((m) => ({ default: m.SupportPage })),
)
const MarketplacePage = lazyRetry(() =>
  import('@/pages/marketplace').then((m) => ({ default: m.MarketplacePage })),
)
const HandloadsPage = lazyRetry(() =>
  import('@/pages/handloads').then((m) => ({ default: m.HandloadsPage })),
)
const ClassificationsPage = lazyRetry(() =>
  import('@/pages/classifications').then((m) => ({ default: m.ClassificationsPage })),
)
const ChallengesPage = lazyRetry(() =>
  import('@/pages/challenges').then((m) => ({ default: m.ChallengesPage })),
)
const ChronographPage = lazyRetry(() =>
  import('@/pages/chronograph').then((m) => ({ default: m.ChronographPage })),
)
const RODashboardPage = lazyRetry(() =>
  import('@/pages/ro-dashboard').then((m) => ({ default: m.RODashboardPage })),
)
const CalendarPage = lazyRetry(() =>
  import('@/pages/calendar').then((m) => ({ default: m.CalendarPage })),
)
const MeetingsPage = lazyRetry(() =>
  import('@/pages/meetings').then((m) => ({ default: m.MeetingsPage })),
)
const DocumentsPage = lazyRetry(() =>
  import('@/pages/documents').then((m) => ({ default: m.DocumentsPage })),
)
const MessagesPage = lazyRetry(() =>
  import('@/pages/messages').then((m) => ({ default: m.MessagesPage })),
)
const CadetsPage = lazyRetry(() =>
  import('@/pages/cadets').then((m) => ({ default: m.CadetsPage })),
)
const SquaddingPage = lazyRetry(() =>
  import('@/pages/squadding').then((m) => ({ default: m.SquaddingPage })),
)
const FamilyPage = lazyRetry(() =>
  import('@/pages/family').then((m) => ({ default: m.FamilyPage })),
)
const PollsPage = lazyRetry(() => import('@/pages/polls').then((m) => ({ default: m.PollsPage })))
const PhotosPage = lazyRetry(() =>
  import('@/pages/photos').then((m) => ({ default: m.PhotosPage })),
)
const WaitingListPage = lazyRetry(() =>
  import('@/pages/waiting-list').then((m) => ({ default: m.WaitingListPage })),
)
const BriefingsPage = lazyRetry(() => import('@/pages/briefings'))
const ExportsPage = lazyRetry(() => import('@/pages/exports'))
const CredentialChainsPage = lazyRetry(() =>
  import('@/pages/credential-chains').then((m) => ({ default: m.CredentialChainsPage })),
)
const CompetitionFinancePage = lazyRetry(() => import('@/pages/competition-finance'))
const ReportsPage = lazyRetry(() =>
  import('@/pages/reports').then((m) => ({ default: m.ReportsPage })),
)
const RunbookPage = lazyRetry(() => import('@/pages/runbook'))
const FaultReportsPage = lazyRetry(() => import('@/pages/fault-reports'))
const CustomFieldsPage = lazyRetry(() => import('@/pages/custom-fields'))
const ClubPortalPage = lazyRetry(() => import('@/pages/club-portal'))
const DqTrackingPage = lazyRetry(() =>
  import('@/pages/dq-tracking').then((m) => ({ default: m.DqTrackingPage })),
)
const ScoreChallengesPage = lazyRetry(() =>
  import('@/pages/score-challenges').then((m) => ({ default: m.ScoreChallengesPage })),
)
const PublicResultsPage = lazyRetry(() =>
  import('@/pages/public-results').then((m) => ({ default: m.PublicResultsPage })),
)
const WebhooksPage = lazyRetry(() =>
  import('@/pages/webhooks').then((m) => ({ default: m.WebhooksPage })),
)
const ApiDocsPage = lazyRetry(() => import('@/pages/api-docs'))
const PricingPage = lazyRetry(() =>
  import('@/pages/pricing').then((m) => ({ default: m.PricingPage })),
)
const StorefrontPage = lazyRetry(() =>
  import('@/pages/storefront').then((m) => ({ default: m.StorefrontPage })),
)
const CheckoutReturnPage = lazyRetry(() =>
  import('@/pages/checkout-return').then((m) => ({ default: m.CheckoutReturnPage })),
)
const SubscribePage = lazyRetry(() =>
  import('@/pages/subscribe').then((m) => ({ default: m.SubscribePage })),
)
const PortalPublicPage = lazyRetry(() =>
  import('@/pages/portal-public').then((m) => ({ default: m.PortalPublicPage })),
)
const CherishedNumbersPage = lazyRetry(() =>
  import('@/pages/cherished-numbers').then((m) => ({ default: m.CherishedNumbersPage })),
)
const PrivacyPage = lazyRetry(() =>
  import('@/pages/privacy').then((m) => ({ default: m.PrivacyPage })),
)
const TermsPage = lazyRetry(() => import('@/pages/terms').then((m) => ({ default: m.TermsPage })))
const AppSupportPage = lazyRetry(() =>
  import('@/pages/app-support').then((m) => ({ default: m.AppSupportPage })),
)
const InvitePage = lazyRetry(() =>
  import('@/pages/invite').then((m) => ({ default: m.InvitePage })),
)

function PageLoader() {
  return (
    <div className="flex items-center justify-center py-16">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-zero-600" />
    </div>
  )
}

/**
 * Wraps children in AppShell when the user is authenticated,
 * otherwise renders children bare (public view).
 */
function OptionalAuthShell({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-zero-600" />
      </div>
    )
  }

  if (user) {
    return <AppShell>{children}</AppShell>
  }

  return <>{children}</>
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/auth"
        element={
          <Suspense fallback={<PageLoader />}>
            <AuthPage />
          </Suspense>
        }
      />
      {/* Discover is accessible without authentication but gets AppShell when logged in */}
      <Route
        path="/discover"
        element={
          <OptionalAuthShell>
            <Suspense fallback={<PageLoader />}>
              <DiscoverPage />
            </Suspense>
          </OptionalAuthShell>
        }
      />
      {/* Public results portal — accessible without authentication */}
      <Route
        path="/results/public"
        element={
          <Suspense fallback={<PageLoader />}>
            <PublicResultsPage />
          </Suspense>
        }
      />
      <Route
        path="/results/public/:competitionId"
        element={
          <Suspense fallback={<PageLoader />}>
            <PublicResultsPage />
          </Suspense>
        }
      />
      {/* Public API documentation — accessible without authentication */}
      <Route
        path="/api/docs"
        element={
          <Suspense fallback={<PageLoader />}>
            <ApiDocsPage />
          </Suspense>
        }
      />
      {/* Public pricing page — accessible without authentication */}
      <Route
        path="/pricing"
        element={
          <Suspense fallback={<PageLoader />}>
            <PricingPage />
          </Suspense>
        }
      />
      {/* Cherished GRID numbers — public browsing, login required for checkout */}
      <Route
        path="/cherished-numbers"
        element={
          <OptionalAuthShell>
            <Suspense fallback={<PageLoader />}>
              <CherishedNumbersPage />
            </Suspense>
          </OptionalAuthShell>
        }
      />
      {/* Public club portal — accessible without authentication */}
      <Route
        path="/portal/:subdomain"
        element={
          <Suspense fallback={<PageLoader />}>
            <PortalPublicPage />
          </Suspense>
        }
      />
      {/* Public legal pages — accessible without authentication */}
      <Route
        path="/privacy"
        element={
          <Suspense fallback={<PageLoader />}>
            <PrivacyPage />
          </Suspense>
        }
      />
      <Route
        path="/terms"
        element={
          <Suspense fallback={<PageLoader />}>
            <TermsPage />
          </Suspense>
        }
      />
      <Route
        path="/app-support"
        element={
          <Suspense fallback={<PageLoader />}>
            <AppSupportPage />
          </Suspense>
        }
      />
      {/* Club invitation accept page — accessible without authentication */}
      <Route
        path="/invite/:token"
        element={
          <Suspense fallback={<PageLoader />}>
            <InvitePage />
          </Suspense>
        }
      />
      {/* Public support page — shows landing for unauthenticated, full ticket system for authenticated */}
      <Route
        path="/support"
        element={
          <Suspense fallback={<PageLoader />}>
            <SupportPage />
          </Suspense>
        }
      />
      <Route
        path="*"
        element={
          <ProtectedRoute>
            <AppShell>
              <Routes>
                <Route
                  path="/"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <HomePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/module/:slug"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ModuleHubPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/profile"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ProfilePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/wallet"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <WalletPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/wallet/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CertificateDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/armoury"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ArmouryPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/armoury/:id"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FirearmDetailPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/ammunition"
                  element={<Navigate to="/armoury?tab=ammunition" replace />}
                />
                <Route
                  path="/shoot-log"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ShootLogPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/stats"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <StatsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/teams"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TeamsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/clubs"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClubsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/competitions"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CompetitionsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/results"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ResultsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/notifications"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <NotificationsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/admin"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <RoleGuard>
                        <AdminPage />
                      </RoleGuard>
                    </Suspense>
                  }
                />
                <Route
                  path="/payments"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PaymentsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/shop"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <StorefrontPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/checkout/return"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CheckoutReturnPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/subscribe"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SubscribePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/marketplace"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MarketplacePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/handloads"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <HandloadsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/classifications"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClassificationsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/challenges"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ChallengesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/chronograph"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ChronographPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/ro-dashboard"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <RODashboardPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/calendar"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CalendarPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/meetings"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MeetingsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/documents"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <DocumentsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/messages"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <MessagesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/cadets"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CadetsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/squadding"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <SquaddingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/family"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FamilyPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/polls"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PollsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/photos"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <PhotosPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/waiting-list"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <WaitingListPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/briefings"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <BriefingsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/exports"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ExportsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/credential-chains"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CredentialChainsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/competition-finance"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CompetitionFinancePage />
                    </Suspense>
                  }
                />
                <Route
                  path="/reports"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ReportsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/runbook"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <RunbookPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/fault-reports"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FaultReportsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/custom-fields"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <CustomFieldsPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/club-portal"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ClubPortalPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/dq-tracking"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <DqTrackingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/score-challenges"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <ScoreChallengesPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/webhooks"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <WebhooksPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/training"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TrainingPage />
                    </Suspense>
                  }
                />
                <Route
                  path="/training-admin"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <TrainingAdminPage />
                    </Suspense>
                  }
                />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </AppShell>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
